// src/contexts/AuthContext.jsx
import { createContext, useState, useContext, useEffect, useMemo } from 'react';
import Cookies from 'js-cookie';
import i18n from '../i18n/config';
import { createAuthUtils } from '../utils/authUtils';
import { createLogger } from '@/utils/logger.js';
import {
    getEndpoints,
    getGatewayUrl,
    setEndpoints,
    clearEndpoints
} from '../config/runtimeEndpoints';
import { resolveRegion, clearRegionMemo } from '../services/regionService';

const logger = createLogger('Shell::Authentication');

// Cookie configuration
const AUTH_TOKEN_COOKIE = 'auth_token';
const COOKIE_OPTIONS = {
    secure: true,
    sameSite: 'strict',
    expires: 7
};

// Initial permissions state
const INITIAL_PERMISSIONS = {
    roles: [],
    features: [],
    capabilities: null,   // null = not yet loaded → auth.can() fails open (gateway still enforces)
    company: [],
    factories: [],
    ous: [],
    machines: []
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [, setToken] = useState(Cookies.get(AUTH_TOKEN_COOKIE));
    const [permissions, setPermissions] = useState(INITIAL_PERMISSIONS);
    const [loading, setLoading] = useState(true);

    // Create auth utilities for MFEs
    const authUtils = useMemo(() => {
        if (!user) return null;
        return createAuthUtils({ user, permissions });
    }, [user, permissions]);

    // ========================================================================
    // User Type Helpers
    // ========================================================================

    const isSuperuser = useMemo(() => {
        return permissions.roles?.includes('superuser') ?? false;
    }, [permissions.roles]);

    const hasAssignedCompany = useMemo(() => {
        return (permissions.company?.length ?? 0) > 0;
    }, [permissions.company]);

    const primaryCompany = useMemo(() => {
        return permissions.company?.[0] ?? null;
    }, [permissions.company]);

    // ========================================================================
    // Initialization
    // ========================================================================

    useEffect(() => {
        const initializeAuth = async () => {
            const sessionCookie = Cookies.get(AUTH_TOKEN_COOKIE);

            if (!sessionCookie) {
                logger.info('No session cookie found');
                resetAuthState();
                setLoading(false);
                return;
            }

            // The cookie lives 7 days; the cached endpoints can disappear sooner
            // (Safari ITP, "clear site data", a private window). Without them
            // there is no gateway to call and no email to resolve one with, so
            // the only honest state is logged out.
            if (!getEndpoints()) {
                logger.warn('Session cookie present but no cached endpoints — signing out');
                clearLocalSession();
                setLoading(false);
                return;
            }

            logger.info('Session cookie found, fetching profile...');
            await fetchProfile();
            setLoading(false);
        };

        initializeAuth();
    }, []);

    // ========================================================================
    // Auth State Management
    // ========================================================================

    const resetAuthState = () => {
        setUser(null);
        setToken(null);
        setPermissions(INITIAL_PERMISSIONS);
    };

    /**
     * Clear all client-side auth artifacts.
     * Runs regardless of whether the backend logout succeeded.
     */
    const clearLocalSession = () => {
        // Notify other contexts (SSE, cache, etc.) before clearing state
        window.dispatchEvent(new CustomEvent('shell:logout'));

        resetAuthState();
        Cookies.remove(AUTH_TOKEN_COOKIE);

        // Clear sessionStorage (SSE client ID)
        sessionStorage.removeItem('sse_client_id');

        // Clear shell-namespaced localStorage (keep theme preference).
        // This also removes the cached endpoints; clearEndpoints() below drops
        // the in-memory copy and window.__APP_CONFIG__ with it.
        Object.keys(localStorage)
            .filter(key => key.startsWith('shell:') && key !== 'shell:theme')
            .forEach(key => localStorage.removeItem(key));

        clearEndpoints();
        clearRegionMemo();

        i18n.changeLanguage(navigator.language.split('-')[0]);
    };

    /**
     * Point the app at the caller's regional backend.
     *
     * Skipped when the device cache already holds this exact address — that is
     * what makes a returning visit free. A different address always resolves,
     * because a different user can belong to a different region.
     *
     * The password never reaches the resolver.
     *
     * @returns {'cached'|'resolved'|false} where the endpoints came from, so a
     *   caller can tell a cache hit apart from a fresh resolve. `login` needs
     *   that distinction: see discardStaleEndpoints below.
     */
    const pinEndpointsFor = async (email) => {
        const cached = getEndpoints();
        const normalised = String(email ?? '').trim().toLowerCase();

        if (cached && cached.email === normalised) {
            logger.info('Endpoints already cached for this address — skipping resolve');
            return 'cached';
        }

        try {
            const { region, gateway, sse } = await resolveRegion(normalised);
            setEndpoints({ email: normalised, region, gateway, sse });
            return 'resolved';
        } catch (error) {
            // No fallback and no retry — a failed resolve is a failed login.
            logger.error(`Region resolve failed (${error.code}):`, error.message);
            return false;
        }
    };

    /**
     * Throw away a cached gateway that just failed to log anyone in.
     *
     * The device cache has no expiry, and `pinEndpointsFor` trusts it without
     * resolving, so a host that moves between visits would otherwise lock the
     * user out for good: every attempt would POST to the dead host, fail, and
     * leave the same cache in place, with clearing site data the only way out
     * and no way for them to know that. Dropping it here turns that into a
     * second attempt that succeeds.
     *
     * Only for a cache hit — a fresh resolve returned the host the resolver
     * currently advertises, so a failure against that is the backend's answer,
     * not a stale pointer, and re-resolving would just hammer a rate-limited
     * endpoint. The in-session memo is deliberately left alone: it makes the
     * retry's resolve free when it holds this address, and it only ever holds a
     * value this page load actually fetched.
     */
    const discardStaleEndpoints = (source) => {
        if (source !== 'cached') return;
        logger.warn('Login failed on cached endpoints — dropping them so the next attempt re-resolves');
        clearEndpoints();
    };

    const login = async (credentials) => {
        // Hoisted so the catch below can see it — a dead cached host surfaces as
        // a rejected fetch, not as a non-ok response.
        let endpointSource = null;

        try {
            // LoginPage sends the address as `username`; `email` is accepted so
            // any other caller works too. Only the address is resolved with —
            // the password never leaves this function.
            const address = credentials?.username ?? credentials?.email;

            endpointSource = await pinEndpointsFor(address);
            if (!endpointSource) {
                return false;
            }

            const loginUrl = `${getGatewayUrl()}${import.meta.env.VITE_AUTH_ENDPOINT}`;
            logger.info('Attempting login...');

            const response = await fetch(loginUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });

            if (!response.ok) {
                logger.error('Login failed with status:', response.status);
                discardStaleEndpoints(endpointSource);
                return false;
            }

            const data = await response.json();

            Cookies.set(AUTH_TOKEN_COOKIE, data.token, COOKIE_OPTIONS);
            logger.info('Session cookie set, fetching user profile');

            return await fetchProfile();
        } catch (error) {
            logger.error('Login error:', error);
            discardStaleEndpoints(endpointSource);
            return false;
        }
    };

    const logout = () => {
        logger.info('Logging out...');

        const currentToken = Cookies.get(AUTH_TOKEN_COOKIE);

        // Fire-and-forget: invalidate server-side session without blocking UI.
        // Endpoints can already be gone (see initializeAuth), in which case
        // there is nowhere to send this and the local clear-down is enough.
        if (currentToken && getGatewayUrl()) {
            const logoutUrl = `${getGatewayUrl()}${import.meta.env.VITE_LOGOUT_ENDPOINT}`;

            fetch(logoutUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${currentToken}`
                }
            }).catch(error => {
                logger.warn('Backend logout request failed:', error.message);
            });
        }

        clearLocalSession();
    };

    const fetchProfile = async () => {
        try {
            const gatewayUrl = getGatewayUrl();
            const currentToken = Cookies.get(AUTH_TOKEN_COOKIE);

            if (!currentToken) {
                logger.warn('No session token found');
                clearLocalSession();
                return false;
            }

            if (!gatewayUrl) {
                logger.warn('No resolved gateway — cannot fetch profile');
                clearLocalSession();
                return false;
            }

            const profileUrl = `${gatewayUrl}${import.meta.env.VITE_PROFILE_ENDPOINT}`;

            const response = await fetch(profileUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${currentToken}`
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    logger.warn('Session expired or invalid');
                    clearLocalSession();
                    return false;
                }
                throw new Error('Failed to fetch profile');
            }

            const data = await response.json();

            const userData = {
                firstName: data.first_name,
                lastName: data.last_name,
                language: data.language
            };

            const userPermissions = {
                roles: data.permissions?.roles || [],
                features: data.permissions?.services ?? data.permissions?.features ?? [],
                // features: [
                //     "dashboard",
                //     "edit_mode",
                //     "flexible_shifts",
                //     "shopfloor_ai",
                //     "notifications",
                //     "user_management"],
                company: data.permissions?.companies || [],
                factories: data.permissions?.factories || [],
                ous: data.permissions?.ous || [],
                machines: data.permissions?.machines || [],
                // input_codes the user may call — TOP-LEVEL on the profile response (gateway
                // ProfileHandler), not under permissions. null when an older gateway omits it
                // → auth.can() fails open (gateway still enforces).
                capabilities: data.capabilities ?? null
            };

            setUser(userData);
            setPermissions(userPermissions);
            setToken(currentToken);

            if (userData.language) {
                await i18n.changeLanguage(userData.language);
                logger.info('Language set to:', userData.language);
            }

            logger.info('Profile loaded', {
                roles: userPermissions.roles,
                features: userPermissions.features,
                companyCount: userPermissions.company.length,
                isSuperuser: userPermissions.roles.includes('superuser')
            });

            return true;
        } catch (error) {
            logger.error('Profile fetch error:', error);
            clearLocalSession();
            return false;
        }
    };

    // ========================================================================
    // Permission Checking
    // ========================================================================

    const hasFeature = (featureName) => {
        return permissions.features?.includes(featureName) || false;
    };

    // Legacy methods for backward compatibility with MFEs
    const hasPermission = (permission) => authUtils?.hasPermission(permission) || false;
    const hasAnyPermission = (roleNames) => authUtils?.hasAnyPermission(roleNames) || false;
    const isInGroup = (groupName) => authUtils?.isInGroup(groupName) || false;

    // ========================================================================
    // Context Value
    // ========================================================================

    const contextValue = {
        // Auth state
        user,
        token: Cookies.get(AUTH_TOKEN_COOKIE),
        loading,
        permissions,
        isAuthenticated: !!user,

        // Auth actions
        login,
        logout,

        // User type helpers
        isSuperuser,
        hasAssignedCompany,
        primaryCompany,

        // Feature checking (preferred)
        hasFeature,

        // Legacy permission checking (for MFE backward compatibility)
        hasPermission,
        hasAnyPermission,
        isInGroup,

        // Auth utilities for MFEs
        authUtils
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;